import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { environment } from 'src/environments/environment';

/** Nilai yang valid untuk satu query param */
type HttpParamValue = string | number | boolean | null | undefined;

/** Objek query params — menerima semua interface/object, tidak membutuhkan index signature */
type HttpParamRecord = object | null;

/** Objek header HTTP: key & value keduanya string */
type HttpHeaderRecord = Record<string, string> | null;

/** Nilai responseType yang didukung Angular HttpClient */
type ResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';

@Injectable({
  providedIn: 'root',
})
export class BaseService {
  constructor(
    private httpClient: HttpClient,
    private storageService: LocalStorageService
  ) {}

  getData<T = any>(
    url: string,
    param?: HttpParamRecord,
    header?: HttpHeaderRecord,
    responseType?: ResponseType
  ): Observable<T> {
    const httpParam = this.generateHttpParam(param);
    const httpHeader = this.generateHttpHeader(header);
    // Cast responseType sebagai 'json' agar TypeScript memilih overload yang tepat;
    // as unknown as Observable<T> memastikan tipe return sesuai generic T
    return this.httpClient
      .get(url, {
        params: httpParam,
        headers: httpHeader,
        responseType: (responseType ?? 'json') as 'json',
      })
      .pipe(
        map((resp) => resp),
        catchError((err) => throwError(err))
      ) as unknown as Observable<T>;
  }

  postData<T = any>(
    url: string,
    body: unknown,
    param?: HttpParamRecord,
    header?: HttpHeaderRecord
  ): Observable<T> {
    const httpParam = this.generateHttpParam(param);
    const httpHeader = this.generateHttpHeader(header);
    return this.httpClient
      .post<T>(url, body, { params: httpParam, headers: httpHeader })
      .pipe(
        map((resp) => resp),
        catchError((err) => throwError(err))
      );
  }

  putData<T = any>(
    url: string,
    body: unknown,
    param?: HttpParamRecord,
    header?: HttpHeaderRecord
  ): Observable<T> {
    const httpParam = this.generateHttpParam(param);
    const httpHeader = this.generateHttpHeader(header);
    return this.httpClient
      .put<T>(url, body, { params: httpParam, headers: httpHeader })
      .pipe(
        map((resp) => resp),
        catchError((err) => throwError(err))
      );
  }

  deleteData<T = any>(
    url: string,
    param?: HttpParamRecord,
    header?: HttpHeaderRecord
  ): Observable<T> {
    const httpParam = this.generateHttpParam(param);
    const httpHeader = this.generateHttpHeader(header);
    return this.httpClient
      .delete<T>(url, { params: httpParam, headers: httpHeader })
      .pipe(
        map((resp) => resp),
        catchError((err) => throwError(err))
      );
  }

  public generateHttpParam(param?: HttpParamRecord | null): HttpParams {
    let result = new HttpParams();

    if (param) {
      const paramRecord = param as Record<string, unknown>;
      Object.keys(paramRecord).forEach((key) => {
        if (
          paramRecord[key] !== null &&
          paramRecord[key] !== undefined &&
          paramRecord[key] !== ''
        ) {
          result = result.set(key, String(paramRecord[key]));
        }
      });
    }

    return result;
  }

  public generateHttpHeader(header?: HttpHeaderRecord | null): HttpHeaders {
    let result = new HttpHeaders();

    if (header) {
      Object.keys(header).forEach((key) => {
        if (
          header[key] !== null &&
          header[key] !== undefined &&
          header[key] !== ''
        ) {
          result = result.set(key, header[key]);
        }
      });
    }
    return result;
  }

  public getDataWithToken<T = any>(
    url: string,
    paramsRequest?: HttpParamRecord,
    requestHeaders?: HttpHeaderRecord
  ): Observable<T> {
    const headers = new HttpHeaders({ ...requestHeaders });
    const params = this.generateHttpParam(paramsRequest);
    return this.httpClient.get<T>(url, { headers, params });
  }

  public postDataWithToken<T = any>(
    url: string,
    requestBody: unknown,
    responseType: ResponseType = 'json'
  ): Observable<T> {
    const headers = new HttpHeaders({});
    // Cast responseType sebagai 'json' untuk overload resolution;
    // as unknown as Observable<T> untuk tipe return yang sesuai generic T
    return this.httpClient.post(url, requestBody, {
      headers,
      responseType: responseType as 'json',
    }) as unknown as Observable<T>;
  }

  public putDataWithToken<T = any>(
    url: string,
    requestBody: unknown,
    requestParam?: HttpParamRecord
  ): Observable<T> {
    const headers = new HttpHeaders({});
    return this.httpClient.put<T>(url, requestBody, {
      headers,
      params: this.generateHttpParam(requestParam),
    });
  }

  public deleteDataWithToken<T = any>(
    url: string,
    requestHeaders?: HttpHeaderRecord
  ): Observable<T> {
    const headers = new HttpHeaders({ ...requestHeaders });
    return this.httpClient.delete<T>(url, { headers });
  }

  public postDataWithAuth<T = any>(
    url: string,
    requestBody: unknown
  ): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Basic ${btoa(environment.durian_pay.api_key + ':')}`,
    });
    return this.httpClient.post<T>(url, requestBody, { headers });
  }

  public getDataWithAuth<T = any>(url: string): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Basic ${btoa(environment.durian_pay.api_key + ':')}`,
    });
    return this.httpClient.get<T>(url, { headers });
  }
}
